import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { matchesSearch } from '@shared';

import * as fromGenerated from '../../_generated';
import {
  AdminUsersActions,
  AdminUsersSortColumn,
  AdminUsersSortDirection,
} from './admin-users.actions';

export const adminUsersFeatureKey = 'adminUsers';

export interface AdminUsersSort {
  column: AdminUsersSortColumn | null;
  direction: AdminUsersSortDirection;
}

export interface AdminUsersState extends EntityState<fromGenerated.AdminUserView> {
  loaded: boolean;
  isLoading: boolean;
  error: string | null;
  sort: AdminUsersSort;
  filter: string;
  /** Lazily-loaded per-row expand payload for the count-cell dropdowns. */
  dropdownPayloads: Record<string, fromGenerated.UserDropdownPayloadView>;
}

const entityAdapter: EntityAdapter<fromGenerated.AdminUserView> =
  createEntityAdapter<fromGenerated.AdminUserView>({
    selectId: (user) => user.id,
    sortComparer: false, // No sorting by default; keep the order of the API response.
  });

export const initialAdminUsersState: AdminUsersState =
  entityAdapter.getInitialState({
    loaded: false,
    isLoading: false,
    error: null,
    sort: { column: 'user', direction: 'asc' }, // default: name ascending
    filter: '',
    dropdownPayloads: {},
  });

/** Privilege order so sorting by role is meaningful, not alphabetical. */
const ROLE_RANK: Record<fromGenerated.UserRole, number> = {
  member: 0,
  admin: 1,
  owner: 2,
  // eslint-disable-next-line camelcase
  super_admin: 3,
};

const compareUsersByColumn = (
  a: fromGenerated.AdminUserView,
  b: fromGenerated.AdminUserView,
  column: AdminUsersSortColumn,
): number => {
  switch (column) {
    case 'user':
      return (a.fullName ?? a.email ?? '')
        .toLowerCase()
        .localeCompare((b.fullName ?? b.email ?? '').toLowerCase());
    case 'role':
      return ROLE_RANK[a.role] - ROLE_RANK[b.role];
    case 'teammates':
      return (a.teammateCount ?? 0) - (b.teammateCount ?? 0);
    case 'groups':
      return (a.groupCount ?? 0) - (b.groupCount ?? 0);
  }
};

export const adminUsersFeature = createFeature({
  name: adminUsersFeatureKey,
  reducer: createReducer(
    initialAdminUsersState,
    on(AdminUsersActions.loadUsers, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    })),
    on(AdminUsersActions.loadUsersSuccess, (state, { users }) =>
      entityAdapter.setAll(users, {
        ...state,
        loaded: true,
        isLoading: false,
        error: null,
      }),
    ),
    on(AdminUsersActions.loadUsersFailure, (state, { error }) => ({
      ...state,
      error,
      isLoading: false,
    })),
    on(AdminUsersActions.setSort, (state, { column, direction }) => ({
      ...state,
      sort: { column, direction },
    })),
    on(AdminUsersActions.setFilter, (state, { filter }) => ({
      ...state,
      filter,
    })),
    on(
      AdminUsersActions.loadDropdownPayloadSuccess,
      (state, { userId, payload }) => ({
        ...state,
        dropdownPayloads: { ...state.dropdownPayloads, [userId]: payload },
      }),
    ),
    on(AdminUsersActions.updateUserRoleSuccess, (state, { user }) =>
      entityAdapter.updateOne(
        { id: user.id, changes: { role: user.role } },
        { ...state, error: null },
      ),
    ),
    on(AdminUsersActions.updateUserRoleFailure, (state, { error }) => ({
      ...state,
      error,
    })),
  ),
  extraSelectors: ({ selectAdminUsersState, selectFilter, selectSort }) => {
    const adapterSelectors = entityAdapter.getSelectors(selectAdminUsersState);

    /** Users whose name or email contains the filter (case- and accent-insensitive). */
    const selectFilteredUsers = createSelector(
      adapterSelectors.selectAll,
      selectFilter,
      (users, filter) =>
        filter.trim()
          ? users.filter((user) =>
              matchesSearch(filter, [user.fullName, user.email]),
            )
          : users,
    );

    /** Filtered users with the active column sort applied (null column = API order). */
    const selectFilteredSortedUsers = createSelector(
      selectFilteredUsers,
      selectSort,
      (users, sort) => {
        if (!sort.column) {
          return users;
        }
        const sign = sort.direction === 'asc' ? 1 : -1;
        const column = sort.column;
        return [...users].sort(
          (a, b) => sign * compareUsersByColumn(a, b, column),
        );
      },
    );

    return {
      ...adapterSelectors,
      selectFilteredUsers,
      selectFilteredSortedUsers,
    };
  },
});
